import { useState, useEffect } from "react"
import { 
  Key, Plus, Trash2, Copy, Check, BarChart3, 
  Zap, Shield, CreditCard, Settings, LogOut,
  Sun, Moon, ChevronRight, AlertCircle, CheckCircle,
  Mail, Lock
} from "lucide-react"
import { toast } from "@/hooks/use-toast"
import { cn } from "@/lib/utils"

const API_URL = import.meta.env.VITE_API_URL || "https://url2md-worker.gpts-creator.workers.dev"

interface ApiKey {
  id: string
  name: string
  key: string
  created: string
  lastUsed: string
  requestsCount: number
  revoked?: boolean
}

interface UsageStats {
  totalRequests: number
  errors: number
  errorRate: number
  rateLimit: {
    limit: number
    remaining: number
    reset: string
  }
}

interface User {
  email: string // effectively the userId/apiKey for display
  apiKey: string
  plan: string
}

const PLANS = [
  { id: "free", name: "Free", price: 0, requests: 100, features: ["100 requests/hour", "Basic support", "1 API key"] },
  { id: "pro", name: "Pro", price: 9, requests: 1000, features: ["1,000 requests/hour", "Priority support", "5 API keys", "Analytics"] },
  { id: "enterprise", name: "Enterprise", price: 49, requests: 10000, features: ["10,000 requests/hour", "Dedicated support", "Unlimited keys", "Custom integrations"] }
]

export default function ApiPortal() {
  const [darkMode, setDarkMode] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [activeTab, setActiveTab] = useState<"overview" | "keys" | "usage" | "billing">("overview")
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [authMethod, setAuthMethod] = useState<"register" | "login">("register")
  
  // Auth Form State
  const [email, setEmail] = useState("")
  const [apiKeyLogin, setApiKeyLogin] = useState("")
  
  // Dashboard State
  const [user, setUser] = useState<User | null>(null)
  const [keys, setKeys] = useState<ApiKey[]>([])
  const [usage, setUsage] = useState<UsageStats | null>(null)
  const [loading, setLoading] = useState(false)
  const [newKeyName, setNewKeyName] = useState("")
  const [copiedKey, setCopiedKey] = useState<string | null>(null)
  const [newlyRegisteredKey, setNewlyRegisteredKey] = useState<string | null>(null)

  useEffect(() => {
    setMounted(true)
    const savedUser = localStorage.getItem("url2md_user")
    if (savedUser) {
      const parsed = JSON.parse(savedUser)
      setUser(parsed)
      setIsAuthenticated(true)
      // Validate session
      validateSession(parsed.apiKey)
    }
  }, [])

  useEffect(() => {
    if (!mounted) return
    if (darkMode) { document.documentElement.classList.add("dark") } 
    else { document.documentElement.classList.remove("dark") }
  }, [darkMode, mounted])

  useEffect(() => {
    if (isAuthenticated && user?.apiKey) {
      fetchKeys()
      fetchUsage()
    }
  }, [isAuthenticated, user])

  const validateSession = async (key: string) => {
    try {
      const res = await fetch(`${API_URL}/api-portal/keys`, {
        headers: { "X-API-Key": key }
      })
      if (!res.ok) throw new Error("Session expired")
    } catch {
      handleLogout()
    }
  }

  const handleRegister = async () => {
    if (!email) return
    setLoading(true)
    try {
      const res = await fetch(`${API_URL}/api-portal/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email })
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || "Registration failed")
      
      setNewlyRegisteredKey(data.key)
      toast({ title: "Registration Successful", description: "Save your API key securely!" })
    } catch (error) {
      toast({ title: "Error", description: error instanceof Error ? error.message : "Failed", variant: "destructive" })
    } finally {
      setLoading(false)
    }
  }

  const handleLogin = async () => {
    if (!apiKeyLogin) return
    setLoading(true)
    try {
      // Verify key by fetching usage
      const res = await fetch(`${API_URL}/api-portal/usage`, {
        headers: { "X-API-Key": apiKeyLogin }
      })
      if (!res.ok) throw new Error("Invalid API Key")
      
      const userData: User = {
        email: "User", // We don't have email from key, so generic name
        apiKey: apiKeyLogin,
        plan: "free"
      }
      
      setUser(userData)
      setIsAuthenticated(true)
      localStorage.setItem("url2md_user", JSON.stringify(userData))
      toast({ title: "Welcome back!", description: "Logged in successfully" })
    } catch (error) {
      toast({ title: "Login Failed", description: "Invalid API Key", variant: "destructive" })
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = () => {
    setUser(null)
    setIsAuthenticated(false)
    setKeys([])
    setUsage(null)
    localStorage.removeItem("url2md_user")
    toast({ title: "Logged out", description: "Come back soon!" })
  }

  const fetchKeys = async () => {
    if (!user?.apiKey) return
    try {
      const res = await fetch(`${API_URL}/api-portal/keys`, {
        headers: { "X-API-Key": user.apiKey }
      })
      const data = await res.json()
      if (Array.isArray(data)) {
        setKeys(data)
      }
    } catch (e) {
      console.error("Failed to fetch keys", e)
    }
  }

  const fetchUsage = async () => {
    if (!user?.apiKey) return
    try {
      const res = await fetch(`${API_URL}/api-portal/usage`, {
        headers: { "X-API-Key": user.apiKey }
      })
      const data = await res.json()
      if (res.ok) {
        setUsage(data)
      }
    } catch (e) {
      console.error("Failed to fetch usage", e)
    }
  }

  const createKey = async () => {
    if (!newKeyName.trim()) {
      toast({ title: "Error", description: "Please enter a key name", variant: "destructive" })
      return
    }
    setLoading(true)
    try {
      const res = await fetch(`${API_URL}/api-portal/keys`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "X-API-Key": user?.apiKey || ""
        },
        body: JSON.stringify({ name: newKeyName })
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || "Failed to create key")
      
      setKeys([...keys, data])
      setNewKeyName("")
      toast({ title: "API Key Created", description: "Key created successfully" })
    } catch (error) {
      toast({ title: "Error", description: error instanceof Error ? error.message : "Failed", variant: "destructive" })
    } finally {
      setLoading(false)
    }
  }

  const revokeKey = async (keyId: string) => {
    try {
      const res = await fetch(`${API_URL}/api-portal/keys/${keyId}`, {
        method: "DELETE",
        headers: { "X-API-Key": user?.apiKey || "" }
      })
      if (res.ok) {
        setKeys(keys.filter(k => k.id !== keyId))
        toast({ title: "Key Revoked", description: "API key has been deactivated" })
      }
    } catch {
      toast({ title: "Error", description: "Failed to revoke key", variant: "destructive" })
    }
  }

  const copyKey = async (key: string) => {
    await navigator.clipboard.writeText(key)
    setCopiedKey(key)
    setTimeout(() => setCopiedKey(null), 2000)
    toast({ title: "Copied!", description: "API key copied to clipboard" })
  }

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat().format(num)
  }

  if (!mounted) return null

  // Auth Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-[#0a0a0f] flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white dark:bg-[#121218] rounded-2xl border border-gray-200 dark:border-gray-800 shadow-xl p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-gray-900 to-gray-800 dark:from-blue-500 dark:to-purple-600 flex items-center justify-center text-white font-bold text-2xl mx-auto mb-4 shadow-lg">M</div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Developer Portal</h1>
              <p className="text-gray-500 dark:text-gray-400 mt-1">Manage your API keys & usage</p>
            </div>

            {newlyRegisteredKey ? (
              <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
                <div className="flex items-center gap-2 mb-2 text-green-700 dark:text-green-400 font-medium">
                  <CheckCircle className="h-5 w-5" /> Registration Successful
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">Here is your API key. Copy it now, you won't see it again!</p>
                <div className="flex items-center gap-2 bg-white dark:bg-black/20 p-2 rounded-lg border border-green-200 dark:border-green-800/50">
                  <code className="flex-1 text-sm font-mono text-gray-800 dark:text-gray-200 break-all">{newlyRegisteredKey}</code>
                  <button onClick={() => copyKey(newlyRegisteredKey)} className="p-1.5 hover:bg-black/5 dark:hover:bg-white/10 rounded">
                    {copiedKey === newlyRegisteredKey ? <Check className="h-4 w-4 text-green-500"/> : <Copy className="h-4 w-4"/>}
                  </button>
                </div>
                <button 
                  onClick={() => {
                    setApiKeyLogin(newlyRegisteredKey)
                    setAuthMethod("login")
                    setNewlyRegisteredKey(null)
                  }}
                  className="w-full mt-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
                >
                  Continue to Login
                </button>
              </div>
            ) : (
              <>
                <div className="flex gap-2 p-1 bg-gray-100 dark:bg-gray-800 rounded-lg mb-6">
                  <button onClick={() => setAuthMethod("register")} className={cn("flex-1 py-2.5 rounded-md text-sm font-medium transition-all", authMethod === "register" ? "bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-white" : "text-gray-600 dark:text-gray-400")}>Register</button>
                  <button onClick={() => setAuthMethod("login")} className={cn("flex-1 py-2.5 rounded-md text-sm font-medium transition-all", authMethod === "login" ? "bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-white" : "text-gray-600 dark:text-gray-400")}>Login</button>
                </div>

                {authMethod === "register" ? (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Email Address</label>
                      <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <button onClick={handleRegister} disabled={loading || !email} className="w-full py-3 bg-gray-900 dark:bg-blue-600 text-white font-medium rounded-xl hover:bg-gray-800 dark:hover:bg-blue-500 transition-all disabled:opacity-50 flex items-center justify-center gap-2">
                      {loading ? "Registering..." : "Get API Key"}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">API Key</label>
                      <input type="text" value={apiKeyLogin} onChange={(e) => setApiKeyLogin(e.target.value)} placeholder="url2md_sk_..." className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <button onClick={handleLogin} disabled={loading || !apiKeyLogin} className="w-full py-3 bg-gray-900 dark:bg-blue-600 text-white font-medium rounded-xl hover:bg-gray-800 dark:hover:bg-blue-500 transition-all disabled:opacity-50 flex items-center justify-center gap-2">
                      {loading ? "Verifying..." : "Access Dashboard"}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Dashboard UI
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#0a0a0f] transition-colors duration-300">
      <header className="sticky top-0 z-50 bg-white/90 dark:bg-[#0a0a0f]/90 backdrop-blur-md border-b border-gray-200 dark:border-gray-800">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gray-900 to-gray-800 dark:from-blue-500 dark:to-purple-600 flex items-center justify-center text-white font-bold text-lg shadow-lg">M</div>
            <div>
              <h1 className="text-lg font-bold text-gray-900 dark:text-white">URL2MD</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">Developer Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => setDarkMode(!darkMode)} className="p-2.5 rounded-xl bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700">
              {darkMode ? <Sun className="h-5 w-5 text-yellow-400" /> : <Moon className="h-5 w-5 text-gray-600" />}
            </button>
            <button onClick={handleLogout} className="p-2.5 rounded-xl bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700">
              <LogOut className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            </button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white dark:bg-[#121218] rounded-xl border border-gray-200 dark:border-gray-800 p-6">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Total Requests</p>
            <p className="text-3xl font-bold text-gray-900 dark:text-white">{formatNumber(usage?.totalRequests || 0)}</p>
          </div>
          <div className="bg-white dark:bg-[#121218] rounded-xl border border-gray-200 dark:border-gray-800 p-6">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Active Keys</p>
            <p className="text-3xl font-bold text-gray-900 dark:text-white">{keys.length}</p>
          </div>
          <div className="bg-white dark:bg-[#121218] rounded-xl border border-gray-200 dark:border-gray-800 p-6">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Rate Limit (Hourly)</p>
            <p className="text-3xl font-bold text-gray-900 dark:text-white">{usage?.rateLimit?.remaining || 0}<span className="text-lg text-gray-500 font-normal"> / {usage?.rateLimit?.limit || 100}</span></p>
          </div>
          <div className="bg-white dark:bg-[#121218] rounded-xl border border-gray-200 dark:border-gray-800 p-6">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Error Rate</p>
            <p className="text-3xl font-bold text-gray-900 dark:text-white">{usage?.errorRate || 0}%</p>
          </div>
        </div>

        <div className="flex gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl mb-6 w-fit">
          {[
            { id: "overview", label: "Overview", icon: BarChart3 },
            { id: "keys", label: "API Keys", icon: Key },
            { id: "usage", label: "Usage", icon: Zap },
            { id: "billing", label: "Billing", icon: CreditCard }
          ].map((tab) => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id as typeof activeTab)} className={cn("px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2", activeTab === tab.id ? "bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-white" : "text-gray-600 dark:text-gray-400")}>
              <tab.icon className="h-4 w-4" /> {tab.label}
            </button>
          ))}
        </div>

        <div className="bg-white dark:bg-[#121218] rounded-xl border border-gray-200 dark:border-gray-800 shadow-lg p-6">
          {activeTab === "overview" && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Quick Start</h2>
              <div className="bg-gray-900 dark:bg-gray-950 rounded-lg p-4 overflow-x-auto">
                <pre className="text-sm text-gray-100 font-mono">
{`curl -X GET "${API_URL}/?url=https://example.com" \\
  -H "X-API-Key: ${user?.apiKey || 'your_key_here'}"`}
                </pre>
              </div>
            </div>
          )}

          {activeTab === "keys" && (
            <div className="space-y-6">
              <div className="flex gap-3">
                <input type="text" value={newKeyName} onChange={(e) => setNewKeyName(e.target.value)} placeholder="Key name" className="flex-1 px-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl" />
                <button onClick={createKey} disabled={loading} className="px-6 py-2 bg-gray-900 dark:bg-blue-600 text-white rounded-xl font-medium">Create Key</button>
              </div>
              <div className="space-y-3">
                {keys.map((k) => (
                  <div key={k.id} className="p-4 border border-gray-200 dark:border-gray-700 rounded-xl flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{k.name}</p>
                      <code className="text-sm text-gray-500 bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded mt-1 block w-fit">{k.key}</code>
                    </div>
                    <button onClick={() => revokeKey(k.id)} className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"><Trash2 className="h-5 w-5" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === "usage" && (
             <div className="text-center py-12 text-gray-500">Usage charts coming soon</div>
          )}

          {activeTab === "billing" && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {PLANS.map((plan) => (
                <div key={plan.id} className="p-6 border border-gray-200 dark:border-gray-700 rounded-xl hover:border-blue-500 transition-colors">
                  <h3 className="font-bold text-lg mb-2">{plan.name}</h3>
                  <p className="text-3xl font-bold mb-4">${plan.price}<span className="text-sm font-normal text-gray-500">/mo</span></p>
                  <ul className="space-y-2 mb-6">
                    {plan.features.map((f, i) => <li key={i} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400"><CheckCircle className="h-4 w-4 text-green-500"/> {f}</li>)}
                  </ul>
                  <button className="w-full py-2 bg-gray-900 dark:bg-blue-600 text-white rounded-xl font-medium">Upgrade</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
